'use client';

import { SetStateAction, Dispatch, useState } from 'react';
import {
  Box,
  Slider as ChakraSlider,
  SliderTrack as ChakraSliderTrack,
  SliderFilledTrack as ChakraSliderFilledTrack,
  SliderThumb as ChakraSliderThumb,
  SliderMark as ChakraSliderMark,
  createMultiStyleConfigHelpers,
  Tooltip,
  forwardRef
} from '@chakra-ui/react';
import { sliderAnatomy } from '@chakra-ui/anatomy';

import Icon from '@/components/atomic/v1/icon/Icon';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(sliderAnatomy.keys);

const baseStyle = definePartsStyle({
  filledTrack: {
    bg: 'form/bg:selected'
  },
  track: {
    h: 3,
    bg: 'form/border',
    _disabled: {
      bg: 'status/disabled/bg'
    }
  }
});

export const sliderTheme = defineMultiStyleConfig({
  baseStyle
});

export type SliderCommonProps = {
  min: number;
  max: number;
  thumbColor?: 'orange' | 'blue';
};

export type SliderProps = SliderCommonProps & {
  step?: number;
  tooltipOption?: 'hover' | 'always' | 'off';
  isSliderEnabled?: boolean;
  stepLabel?: number;
  prependToStepLabel?: string;
  appendToStepLabel?: string;
  setSliderValue?: Dispatch<SetStateAction<number>>;
  sliderValue: number | string;
  focusThumbOnChange?: boolean;
  defaultValue?: number;
  isStepLabelVisible?: boolean;
  isFirstAndLastLabelVisible?: boolean;
  isFormatCurrency?: boolean;
};

/**
 * Low level myOMERS Slider component.
 *
 * @param min (left) and @param max (right) values are required.
 * To change the color of the thumb use @param thumbColor.
 * @param step is how many numbers you want to skip between the min and max values.
 * If you want the slider to move by 2 decimal places, use @param step={0.01}.
 * To enable the Slider use @param isSliderEnabled.
 * @param prependToStepLabel and @param appendToStepLabel are used to add a prefix or suffix to the step labels.
 * @param setSliderValue is the setState from useState hook to pass the value of the slider from the Parent.
 * The default value of useState hook would be the default value of the slider when it starts
 * Example usage:
 * @param sliderValue is the value of the slider.
 * @param focusThumbOnChange From Chakra Docs: We recommend disabling focusThumbOnChange on the Slider, so the NumberInput won't lose focus after input.
 *  ```
 *  <Slider min={60} max={70} />
 * ```
 */
// Function to generate slider marks
function generateStepLabels(
  stepLabel: number,
  min: number,
  max: number,
  prependToStepLabelProp: string,
  appendToStepLabelProp: string
) {
  // this condition is to prevent the slider from rendering if the min value is greater than or equal to the max value
  if (min >= max) {
    return null;
  }

  return [...Array(Math.ceil((max - min) / stepLabel + 1))].map((_x, i) => {
    const displayLabel = prependToStepLabelProp + (min + stepLabel * i).toString() + appendToStepLabelProp;

    return (
      <ChakraSliderMark
        // eslint-disable-next-line react/no-array-index-key
        key={min + stepLabel * i}
        value={min + stepLabel * i}
        textAlign="left"
        mt="5"
        fontSize="sm"
        // Single digit will have a margin-left of 1
        ml={min + stepLabel * i < 10 ? '1' : `${-1 * displayLabel.length}px`}
      >
        {`${prependToStepLabelProp}${min + stepLabel * i}${appendToStepLabelProp}`}
      </ChakraSliderMark>
    );
  });
}

const Slider = forwardRef<SliderProps, 'input'>(
  (
    {
      min,
      max,
      step = 1,
      thumbColor = 'orange',
      tooltipOption = 'off',
      isSliderEnabled = true,
      stepLabel = 1,
      prependToStepLabel = '',
      appendToStepLabel = '',
      setSliderValue,
      sliderValue,
      focusThumbOnChange = true,
      name,
      defaultValue,
      isStepLabelVisible = true,
      isFirstAndLastLabelVisible = false,
      isFormatCurrency = false
    },
    ref
  ) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
      <ChakraSlider
        aria-label="slider"
        min={min}
        max={max}
        step={step}
        isDisabled={!isSliderEnabled}
        onChange={(val) => (setSliderValue ? setSliderValue(val) : undefined)}
        // Show/hide tooltip based on hover state
        onMouseEnter={tooltipOption === 'hover' ? () => setShowTooltip(true) : undefined}
        onMouseLeave={tooltipOption === 'hover' ? () => setShowTooltip(false) : undefined}
        data-testid={`slider-${name ?? defaultValue}`}
        ref={ref}
        focusThumbOnChange={focusThumbOnChange}
        value={Number(sliderValue)}
      >
        {isStepLabelVisible && generateStepLabels(stepLabel, min, max, prependToStepLabel, appendToStepLabel)}
        {isFirstAndLastLabelVisible && (
          <>
            <ChakraSliderMark value={min} pt={6}>{`${prependToStepLabel}${
              isFormatCurrency
                ? min.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })
                : min
            }`}</ChakraSliderMark>
            <ChakraSliderMark value={max} pt={6} style={{ transform: 'translateX(-100%)' }}>{`${prependToStepLabel}${
              isFormatCurrency
                ? max.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })
                : max
            }`}</ChakraSliderMark>
          </>
        )}
        <ChakraSliderTrack borderRadius="full" style={{ width: '110%' }}>
          <ChakraSliderFilledTrack
            style={{ width: '110%' }}
            bgColor={isSliderEnabled ? 'form/bg:selected' : 'form/border'}
          />
        </ChakraSliderTrack>
        <Tooltip
          hasArrow
          mb="3"
          bg="navy.100"
          color="white"
          placement="top"
          isOpen={tooltipOption === 'always' || showTooltip}
          label={`${prependToStepLabel}${sliderValue}${appendToStepLabel}`}
        >
          <ChakraSliderThumb ml="2">
            <Box>
              <Icon
                name="slider-thumb"
                color="icon/default"
                secondaryColor={isSliderEnabled ? `${thumbColor}.100` : 'grey.30'}
                size={52}
              />
            </Box>
          </ChakraSliderThumb>
        </Tooltip>
      </ChakraSlider>
    );
  }
);

export default Slider;
